import { LEADS_API_URL, LEADS_FILTER_URL } from 'constants/ApiConstants';
import { ASSIGNEE } from 'constants/FilterConstants';

import type { ApiError, ApiFailureAction, MetaAction } from 'modules/global/generic.types';
import type { DetailedIntervention } from 'modules/interventions/types/projections/detailed-intervention';
import * as types from 'modules/leads/actionTypes';
import type * as LeadsTypes from 'modules/leads/types';

export const fetchLeads = (
  { page = 0, size = 10, assignedUserIds, ...params }: LeadsTypes.FetchLeadsParams,
  meta: LeadsTypes.LeadsMeta = {}
): LeadsTypes.FetchLeadsAction => ({
  type: types.FETCH_REQUEST,
  payload: {
    request: {
      method: 'GET',
      url: LEADS_FILTER_URL,
      params: {
        page,
        size,
        projection: 'page',
        ...params,
        assigneeIds: assignedUserIds?.map(({ id }) => id),
        notExist: params.notExist?.includes(ASSIGNEE) ? 'assigneeId' : undefined,
      },
    },
  },
  meta,
});

export const fetchSuccess = (response: LeadsTypes.FetchLeadsResponse, meta: LeadsTypes.LeadsMeta = {}): LeadsTypes.FetchLeadsSuccessAction => ({
  type: types.FETCH_SUCCESS,
  payload: response,
  meta,
});

export const fetchFailure = (error: ApiError, meta: MetaAction = {}): ApiFailureAction => ({
  type: types.FETCH_FAILURE,
  payload: error,
  error: true,
  meta,
});

export const assign = ({ leadId, userId }: LeadsTypes.AssignPayload, meta: MetaAction = {}): LeadsTypes.AssignLeadAction => ({
  type: types.ASSIGN_REQUEST,
  payload: {
    request: {
      method: 'PATCH',
      url: `${LEADS_API_URL}/${leadId}/assignee`,
      data: {
        userId: userId || null,
      },
    },
    leadId,
    userId: userId || null,
  },
  meta: { ...meta, entityId: leadId },
});

export const assignSuccess = (lead: LeadsTypes.PaginatedLead, meta: MetaAction = {}): LeadsTypes.LeadSuccessAction => ({
  type: types.ASSIGN_SUCCESS,
  payload: {
    lead,
  },
  meta,
});

export const assignFailure = (error: ApiError, meta: MetaAction = {}): ApiFailureAction => ({
  type: types.ASSIGN_FAILURE,
  payload: error,
  error: true,
  meta,
});

export const setStatus = ({ leadId, status }: LeadsTypes.SetStatusPayload, meta: MetaAction = {}): LeadsTypes.SetLeadStatusAction => ({
  type: types.SET_STATUS_REQUEST,
  payload: {
    request: {
      method: 'PATCH',
      url: `${LEADS_API_URL}/${leadId}/status`,
      data: {
        status,
      },
    },
    leadId,
    status,
  },
  meta: { ...meta, entityId: leadId },
});

export const setStatusSuccess = (lead: LeadsTypes.PaginatedLead, meta: MetaAction = {}): LeadsTypes.LeadSuccessAction => ({
  type: types.SET_STATUS_SUCCESS,
  payload: {
    lead,
  },
  meta,
});

export const setStatusFailure = (error: ApiError, meta: MetaAction = {}): ApiFailureAction => ({
  type: types.SET_STATUS_FAILURE,
  payload: error,
  error: true,
  meta,
});

export const createDirectMessage = (
  { leadId, referenceId }: LeadsTypes.CreateDirectMessagePayload,
  meta: MetaAction = {}
): LeadsTypes.CreateDirectMessageAction => ({
  type: types.CREATE_DM_REQUEST,
  payload: {
    request: {
      method: 'POST',
      url: `${LEADS_API_URL}/${leadId}/directMessage`,
      data: {
        referenceId,
      },
    },
    leadId,
    referenceId,
  },
  meta: { ...meta, entityId: leadId },
});

export const createDirectMessageSuccess = (intervention: DetailedIntervention, meta: MetaAction = {}): LeadsTypes.CreateDirectMessageSuccessAction => ({
  type: types.CREATE_DM_SUCCESS,
  payload: {
    intervention,
  },
  meta,
});

export const createDirectMessageFailure = (error: ApiError, meta: MetaAction = {}): ApiFailureAction => ({
  type: types.CREATE_DM_FAILURE,
  payload: error,
  error: true,
  meta,
});

export const deleteLead = (leadId: string, meta: MetaAction = {}): LeadsTypes.DeleteLeadAction => ({
  type: types.DELETE_LEAD_REQUEST,
  payload: {
    request: {
      method: 'DELETE',
      url: `${LEADS_API_URL}/${leadId}`,
    },
    leadId,
  },
  meta: { ...meta, entityId: leadId },
});

export const deleteLeadSuccess = (leadId: string, meta: MetaAction = {}): LeadsTypes.DeleteLeadSuccessAction => ({
  type: types.DELETE_LEAD_SUCCESS,
  payload: leadId,
  meta,
});

export const deleteLeadFailure = (error: ApiError, meta: MetaAction = {}): ApiFailureAction => ({
  type: types.DELETE_LEAD_FAILURE,
  payload: error,
  error: true,
  meta,
});
